import { createContext, FC, PropsWithChildren, useContext, useLayoutEffect, useRef } from 'react';
import { Theme, ThemeProvider, useColorScheme, useTheme } from '@mui/material';

/**
 * Dark Mode fuers CRM (Entscheid Alex 28.10.2026, Decision #248).
 *
 * Bedienung: Konto-Menue der Kopfleiste, Hell / Dunkel / System, Standard
 * Hell, gespeichert im Browser (`FARBMODUS_SCHLUESSEL`). Das aktive Schema
 * steht als `data-mui-color-scheme` am <html>; alle Farben sind CSS-Variablen
 * und folgen dem Attribut — auch Dialoge, die sich am <body> oeffnen.
 *
 * 🔥 HELL BLEIBEN: Beratungsstrecke, Kundenseiten, Login (und die
 * Google-Rueckkehrseite). Sie tragen `HellSperre`. Die Sperre
 *   1. setzt das Attribut am <html> auf `light`, solange sie steht, und haelt
 *      es dort (MUI setzt es bei Schema-Wechseln neu — ein Beobachter dreht
 *      es zurueck),
 *   2. stellt beim Verlassen das Schema wieder her, das MUI fuehrt,
 *   3. legt fuer ihren Teilbaum die HELLE Palette in `theme.palette`
 *      (`hellFixiert`) — JS-Leser wie Recharts bekommen sonst die dunkle,
 *      weil CssVarsProvider das aktive Schema einmischt.
 * Die gespeicherte Wahl bleibt unangetastet (kein `setMode`).
 *
 * Verschachtelte Provider (Sidebar-Rahmen, `CrmStil` der Strecke) erben das
 * Schema vom aeusseren CssVarsProvider; innerhalb der Sperre nehmen sie ihr
 * Theme deshalb durch `useHellGesperrt()` + `hellFixiert()`.
 *
 * Das Ladeskript in `public/index.html` setzt das Attribut VOR React (kein
 * Aufblitzen) und kennt dieselben Pfade — `GESPERRTE_PFADE` ist die Quelle,
 * `farbschema.test.tsx` haelt beide gleich.
 */

/** Speicher-Schluessel der Wahl im Browser (`light` | `dark` | `system`). */
export const FARBMODUS_SCHLUESSEL = 'bw-farbmodus';

/** Attribut am <html>, das das Schema traegt (`cssVariables.colorSchemeSelector`). */
export const SCHEMA_ATTRIBUT = 'data-mui-color-scheme';

/**
 * Pfade, die immer hell bleiben — Beratungsstrecke, Login, Kundenseiten,
 * Google-Rueckkehr. Muss zu den `HellSperre`-Huellen im Router passen.
 */
export const GESPERRTE_PFADE =
  /^\/(auth|unterlagen|termin|buchen|google-oauth-complete)(\/|$)|^\/home\/customers\/customer(\/|$)/;

const cache = new WeakMap<Theme, Theme>();

/**
 * Dasselbe Theme mit der hellen Palette in beiden Schemata. Ein
 * verschachtelter CssVarsProvider mischt `colorSchemes[<aktives Schema>]` in
 * `theme.palette` — hier ist das in jedem Fall hell. Die CSS-Variablen kommen
 * weiter vom <html> (die Sperre haelt es hell). Gecacht, damit der Provider
 * nicht bei jedem Rendern ein neues Theme sieht.
 */
export const hellFixiert = (theme: Theme): Theme => {
  const vorhanden = cache.get(theme);
  if (vorhanden) return vorhanden;
  const schemata = (theme as Theme & { colorSchemes?: Record<string, unknown> }).colorSchemes;
  const fixiert = schemata?.light
    ? ({ ...theme, colorSchemes: { ...schemata, dark: schemata.light } } as Theme)
    : theme;
  cache.set(theme, fixiert);
  return fixiert;
};

const HellGesperrtKontext = createContext(false);

/** true innerhalb einer `HellSperre` — fuer verschachtelte ThemeProvider. */
export const useHellGesperrt = (): boolean => useContext(HellGesperrtKontext);

let offeneSperren = 0;

/** Haelt dieses Gebiet hell (Strecke, Kundenseiten, Login). */
export const HellSperre: FC<PropsWithChildren> = ({ children }) => {
  const theme = useTheme();
  const { colorScheme } = useColorScheme();
  const schemaRef = useRef(colorScheme);
  schemaRef.current = colorScheme;

  useLayoutEffect(() => {
    const html = document.documentElement;
    offeneSperren += 1;
    const hell = () => {
      if (html.getAttribute(SCHEMA_ATTRIBUT) !== 'light') html.setAttribute(SCHEMA_ATTRIBUT, 'light');
    };
    hell();
    const beobachter = new MutationObserver(hell);
    beobachter.observe(html, { attributes: true, attributeFilter: [SCHEMA_ATTRIBUT] });
    return () => {
      beobachter.disconnect();
      offeneSperren -= 1;
      if (offeneSperren === 0 && schemaRef.current) html.setAttribute(SCHEMA_ATTRIBUT, schemaRef.current);
    };
  }, []);

  return (
    <HellGesperrtKontext.Provider value>
      <ThemeProvider theme={hellFixiert(theme)}>{children}</ThemeProvider>
    </HellGesperrtKontext.Provider>
  );
};

/** Die drei Wahlen im Konto-Menue, in Anzeige-Reihenfolge. */
export const FARBMODI = [
  { wert: 'light', label: 'Hell' },
  { wert: 'dark', label: 'Dunkel' },
  { wert: 'system', label: 'System' },
] as const;

export type Farbmodus = (typeof FARBMODI)[number]['wert'];
